import { useInfiniteQuery } from "@tanstack/react-query";
import {
  getMatchingSearchAgency,
  getMatchingSearchExpert,
  getMatchingSearchHospital,
  getAgencyDetail,
  getReviewList,
  getPortfolioList,
  getBookmarkedAgencies,
} from "apis/matching";
import QUERY_KEYS from "config/queryKey";
import { ExperienceLevel } from "models/User";
import { getIsUser } from "utils/utility";
import { useQuery } from "@tanstack/react-query";
import { GetReviewListResponse, GetPortfolioResponse } from "models/matching";

const isUser = getIsUser();

export const useMatchingHospitalQuery = ({
  selectedCategory,
  selectedFilter,
  name,
}: {
  selectedCategory: string;
  selectedFilter: string;
  name: string;
}) => {
  return useInfiniteQuery({
    queryKey: [
      QUERY_KEYS.matching,
      { type: "hospital" },
      selectedCategory,
      name,
      selectedFilter,
    ],
    queryFn: ({ pageParam }) =>
      getMatchingSearchHospital({
        page: pageParam,
        name,
        department: selectedFilter,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.page + 1 : null,
    select: (data) => (data.pages ?? []).flatMap((page) => page.content),
    enabled: selectedCategory === "hospital" && isUser,
  });
};

export const useMatchingAgencyQuery = ({
  category,
  agencyName,
  minPrice,
  maxPrice,
}: {
  category: string;
  agencyName: string;
  minPrice?: number;
  maxPrice?: number;
}) => {
  return useInfiniteQuery({
    queryKey: [
      QUERY_KEYS.matching,
      { type: "agency" },
      agencyName,
      category,
      minPrice,
      maxPrice,
    ],
    queryFn: ({ pageParam }) =>
      getMatchingSearchAgency({
        page: pageParam,
        agencyName,
        category,
        minPrice,
        maxPrice,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.page + 1 : null,
    select: (data) => (data.pages ?? []).flatMap((page) => page.content),
    enabled: isUser,
  });
};

export const useMatchingExpertQuery = ({
  selectedCategory,
  selectedFilter,
  name,
}: {
  selectedCategory: string;
  selectedFilter: ExperienceLevel | string;
  name: string;
}) => {
  return useInfiniteQuery({
    queryKey: [
      QUERY_KEYS.matching,
      { type: "expert" },
      selectedCategory,
      name,
      selectedFilter,
    ],
    queryFn: ({ pageParam }) =>
      getMatchingSearchExpert({
        page: pageParam,
        name,
        experienceLevel: selectedFilter,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.page + 1 : null,
    select: (data) => (data.pages ?? []).flatMap((page) => page.content),
    enabled: selectedCategory === "expert" && isUser,
  });
};

export const useAgencyDetailQuery = (agencyId: number) => {
  return useQuery({
    queryKey: [QUERY_KEYS.postDetail, agencyId],
    queryFn: () => getAgencyDetail(agencyId),
    enabled: !!agencyId,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: true,
  });
};

export const useAgencyReviewQuery = (agencyId: number) => {
  return useInfiniteQuery<GetReviewListResponse>({
    queryKey: ["reviewList", agencyId],
    queryFn: ({ pageParam = 0 }) =>
      getReviewList(agencyId, pageParam as number),
    initialPageParam: 0, // ✅ 필수
    getNextPageParam: (lastPage) => {
      return lastPage.hasNextPage ? lastPage.page + 1 : undefined;
    },
    enabled: !!agencyId,
  });
};

export const useAgencyPortfolioQuery = (agencyId: number) => {
  return useInfiniteQuery<GetPortfolioResponse>({
    queryKey: ["portfolio", agencyId],
    queryFn: ({ pageParam = 0 }) =>
      getPortfolioList(agencyId, pageParam as number),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      return lastPage.hasNextPage ? lastPage.page + 1 : undefined;
    },
    enabled: !!agencyId,
  });
};

export const useBookmarkedAgenciesQuery = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.matching, { type: "bookmarked" }],
    queryFn: () => getBookmarkedAgencies(),
    enabled: isUser,
    refetchOnMount: "always",
    staleTime: 0,
  });
};
